/**
 * AudioService refinado para notificaciones de alta calidad.
 * Utiliza síntesis aditiva y envolventes suaves para un sonido premium.
 */

class AudioService {
    private context: AudioContext | null = null;
    private masterGain: GainNode | null = null;

    private async initContext() {
        if (!this.context) {
            this.context = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
            this.masterGain = this.context.createGain();
            this.masterGain.connect(this.context.destination);
            this.masterGain.gain.value = 0.5; // Volumen maestro cómodo
        }

        if (this.context.state === 'suspended') {
            await this.context.resume();
        }
    }

    /**
     * Crea un sonido armónico con múltiples frecuencias.
     */
    private async playNote(freq: number, type: OscillatorType, duration: number, volume: number = 0.1) {
        await this.initContext();
        if (!this.context || !this.masterGain) return;

        const now = this.context.currentTime;
        const osc = this.context.createOscillator();
        const gain = this.context.createGain();

        osc.type = type;
        osc.frequency.setValueAtTime(freq, now);

        // Envolvente ADSR simple
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(volume, now + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(now);
        osc.stop(now + duration);
    }

    /**
     * Tono sutil pero nítido para avisar que el contador está parado.
     */
    async playReminder(theme: string = 'classic') {
        try {
            if (theme === 'nature') {
                await this.playNote(329.63, 'sine', 1.0, 0.05); // Mi4 suave
            } else if (theme === 'modern') {
                await this.playNote(880, 'sine', 0.2, 0.1); // La5 corto
            } else {
                await this.playNote(440, 'sine', 0.5, 0.15); // La4
                await this.playNote(554.37, 'sine', 0.5, 0.1); // Do#5
            }
        } catch (e) {
            console.warn("Audio blocked", e);
        }
    }

    /**
     * Campana armónica clara para la hora / media hora.
     */
    async playClockChime(theme: string = 'classic') {
        try {
            if (theme === 'nature') {
                // Sonido de madera / percusión suave
                this.playNote(440, 'triangle', 0.4, 0.1);
                setTimeout(() => this.playNote(330, 'triangle', 0.6, 0.08), 150);
            } else if (theme === 'modern') {
                // Pulso electrónico elegante
                this.playNote(1200, 'sine', 0.1, 0.15);
                setTimeout(() => this.playNote(1200, 'sine', 0.5, 0.1), 150);
            } else {
                this.playNote(1046.50, 'triangle', 1.5, 0.2); // Do6
                this.playNote(2093, 'sine', 0.8, 0.05); // Octava
                this.playNote(523.25, 'sine', 2.0, 0.1); // Do5 (Sustain)
            }
        } catch (e) {
            console.warn("Audio blocked", e);
        }
    }

    /**
     * Sonido de éxito al finalizar una sesión productiva.
     */
    async playSuccess(theme: string = 'classic') {
        try {
            if (theme === 'nature') {
                this.playNote(587.33, 'sine', 0.5, 0.1);
                setTimeout(() => this.playNote(783.99, 'sine', 0.8, 0.12), 150);
            } else if (theme === 'modern') {
                this.playNote(1000, 'sine', 0.05, 0.1);
                setTimeout(() => this.playNote(1200, 'sine', 0.05, 0.1), 50);
                setTimeout(() => this.playNote(1400, 'sine', 0.2, 0.15), 100);
            } else {
                this.playNote(523.25, 'sine', 0.1, 0.1);
                setTimeout(() => this.playNote(659.25, 'sine', 0.1, 0.1), 100);
                setTimeout(() => this.playNote(783.99, 'sine', 0.4, 0.15), 200);
            }
        } catch (e) {
            console.warn("Audio blocked", e);
        }
    }
}

export const audioService = new AudioService();
